import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const MAX_DESCRIPTION_LENGTH = 2000;

const Step1DetailsForm = ({ nextStep, updateFormData, initialData }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [description, setDescription] = useState(initialData.project_description || "");
  const [characterCount, setCharacterCount] = useState((initialData.project_description || "").length);

  const countryId = 1; // India

  let headers = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const requestBody = {
    page_number: 1,
    page_size: 30,
    order_by: "ASC"
  };

  useEffect(() => {
    fetchStates();
    fetchtypes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStates = () => {
    axios
      .post(`${baseUrl}/admin/states/${countryId}`, requestBody, headers)
      .then(res => {
        setStates(res.data.states || []);
      })
      .catch(err => console.error(err));
  }

  const fetchtypes = () => {
    try {
      const response = axios.get(`${baseUrl}/all/property/types`, headers);
      response.then((res) => {
        setPropertyTypes(res.data.propertyTypes || []);
      });
    }
    catch (error) {
      console.error("Error fetching property types:", error);
    }
  }

  const fetchCities = (stateId) => {
    try {
      const response = axios.post(`${baseUrl}/admin/cities/${stateId}`, requestBody, headers);
      response.then((res) => {
        setCities(res.data.cities || []);
      });
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const schema = Yup.object({
    project_name: Yup.string().required("Project name is required"),
    project_description: Yup.string()
      .required("Description is required")
      .max(MAX_DESCRIPTION_LENGTH, "Description too long"),
    starting_cost: Yup.string().required("Starting cost is required"),
    city_name: Yup.string().required("City is required"),
    state_name: Yup.string().required("State is required"),
    country_name: Yup.string().required("Country is required"),
    pin_code: Yup.string().required("Pin Code is required"),
    area_name: Yup.string().required("Area name is required"),
    type_id: Yup.string().required("Property type is required"),
  });

  return (
    <div className="container bg-white p-4 p-md-5 rounded-4 shadow-sm" style={{ maxWidth: "900px" }}>
      <h3 className="mb-4 fw-bold text-primary border-bottom pb-2">Step 1: Project Details</h3>

      <Formik
        initialValues={{
          project_name: initialData.project_name || "",
          project_description: initialData.project_description || "",
          starting_cost: initialData.starting_cost || "",
          city_name: initialData.city_name || "",
          state_name: initialData.state_name || "",
          country_name: "India",
          pin_code: initialData.pin_code || "",
          area_name: initialData.area_name || "",
          type_id: initialData.type_id || "",
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          updateFormData(values);
          nextStep();
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form className="row g-4">
            {/* Project Name */}
            <div className="col-12">
              <label htmlFor="project_name" className="form-label fw-semibold">
                Project Name
              </label>
              <Field
                name="project_name"
                className="form-control form-control-lg border rounded-3 shadow-sm"
                placeholder="Enter project name"
              />
              <ErrorMessage name="project_name" component="div" className="text-danger small mt-1" />
            </div>

            {/* Project Description (with ReactQuill) */}
            <div className="col-12">
              <label htmlFor="project_description" className="form-label fw-semibold">
                Project Description
              </label>
              <div className="border rounded-3 overflow-hidden">
                <ReactQuill
                  theme="snow"
                  className="bg-white"
                  value={description}
                  onChange={(value) => {
                    setDescription(value);
                    setFieldValue("project_description", value);
                    setCharacterCount(value.length);
                  }}
                />
              </div>
              <div className="d-flex justify-content-between mt-2 small text-muted">
                <span>{characterCount}/{MAX_DESCRIPTION_LENGTH} characters</span>
                {characterCount > MAX_DESCRIPTION_LENGTH && (
                  <span className="text-danger">Description too long</span>
                )}
              </div>
              <ErrorMessage name="project_description" component="div" className="text-danger small mt-1" />
            </div>

            {/* Starting Cost and PIN Code */}
            <div className="col-md-6">
              <label htmlFor="starting_cost" className="form-label fw-semibold">
                Starting Cost (e.g. ₹12 Cr)
              </label>
              <Field
                name="starting_cost"
                className="form-control form-control-lg border rounded-3 shadow-sm"
                placeholder="₹12 Cr"
              />
              <ErrorMessage name="starting_cost" component="div" className="text-danger small mt-1" />
            </div>

            <div className="col-md-6">
              <label htmlFor="pin_code" className="form-label fw-semibold">
                PIN Code
              </label>
              <Field
                name="pin_code"
                className="form-control form-control-lg border rounded-3 shadow-sm"
                placeholder="e.g. 110092"
              />
              <ErrorMessage name="pin_code" component="div" className="text-danger small mt-1" />
            </div>

            {/* property type */}
            <div className="col-md-4">
              <label htmlFor="type_id" className="form-label fw-semibold">
                  Property Type
              </label>
              <Field
                as="select"
                name="type_id"
                className="form-select form-select-lg border rounded-3 shadow-sm"
              >
                <option value="">Select Property Type</option>
                  {propertyTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
              </Field>
              <ErrorMessage name="type_id" component="div" className="text-danger small mt-1" />
            </div>

            {/* State */}
            <div className="col-md-4">
              <label htmlFor="state_name" className="form-label fw-semibold">
                State
              </label>
              <Field
                as="select"
                name="state_name"
                className="form-select form-select-lg border rounded-3 shadow-sm"
                onChange={(e) => {
                  const selectedState = e.target.value;
                  setFieldValue("state_name", selectedState);
                  setFieldValue("city_name", "");
                  const state = states.find((s) => s.name === selectedState);
                  if (state) {
                    fetchCities(state.id);
                  }
                }}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="state_name" component="div" className="text-danger small mt-1" />
            </div>

            {/* City */}
            <div className="col-md-4">
              <label htmlFor="city_name" className="form-label fw-semibold">
                City
              </label>
              <Field
                as="select"
                name="city_name"
                className="form-select form-select-lg border rounded-3 shadow-sm"
              >
                <option value="">Select City</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="city_name" component="div" className="text-danger small mt-1" />
            </div>

            {/* Area Name */}
            <div className="col-12">
              <label htmlFor="area_name" className="form-label fw-semibold">
                Area/Locality
              </label>
              <Field
                name="area_name"
                className="form-control form-control-lg border rounded-3 shadow-sm"
                placeholder="e.g. Green Meadows"
              />
              <ErrorMessage name="area_name" component="div" className="text-danger small mt-1" />
            </div>

            {/* Submit Button */}
            <div className="col-12 d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-primary px-4 py-2 fw-semibold shadow rounded-3"
                disabled={isSubmitting || characterCount > MAX_DESCRIPTION_LENGTH}
              >
                Next
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Step1DetailsForm;